import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, ShieldCheck, Trophy, FileText, Eye } from "lucide-react";

const PURCHASE_STATUS = {
    pending: { label: "Pendiente", cls: "bg-yellow-200" },
    validated: { label: "Validado", cls: "bg-mint" },
    rejected: { label: "Rechazado", cls: "bg-red-200" },
};

const RAFFLE_STATUS = {
    pending: { label: "Pendiente", cls: "bg-yellow-200" },
    approved: { label: "Aprobada", cls: "bg-mint" },
    rejected: { label: "Rechazada", cls: "bg-red-200" },
    drawn: { label: "Sorteada", cls: "bg-coral text-white" },
};

function backendImg(url) {
    if (!url) return null;
    if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
}

export default function AdminDashboard() {
    const [tab, setTab] = useState("purchases");
    const [stats, setStats] = useState(null);
    const [purchases, setPurchases] = useState([]);
    const [raffles, setRaffles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewReceipt, setViewReceipt] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [s, p, r] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/purchases"),
                api.get("/admin/raffles"),
            ]);
            setStats(s.data);
            setPurchases(p.data);
            setRaffles(r.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const act = async (url, msg) => {
        try {
            await api.patch(url);
            toast.success(msg);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const drawWinner = async (raffleId) => {
        try {
            await api.post(`/admin/raffles/${raffleId}/draw`);
            toast.success("¡Sorteo realizado!");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-dashboard">
            <div className="mb-8">
                <div className="text-xs uppercase tracking-[0.3em] font-bold opacity-60 mb-2 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" strokeWidth={3} /> Panel de control
                </div>
                <h1 className="font-display font-black text-5xl uppercase">Administración</h1>
            </div>

            {/* Stats */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="admin-stats">
                    {[
                        { k: "total_users", l: "Usuarios" },
                        { k: "total_raffles", l: "Rifas" },
                        { k: "pending_raffles", l: "Rifas pendientes", hl: true },
                        { k: "approved_raffles", l: "Rifas activas" },
                        { k: "pending_purchases", l: "Pagos pendientes", hl: true },
                        { k: "validated_purchases", l: "Pagos validados" },
                    ].map((s) => (
                        <div key={s.k} className={`brut-card p-4 ${s.hl ? "bg-mint" : ""}`}>
                            <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{s.l}</div>
                            <div className="font-display font-black text-3xl">{stats[s.k]}</div>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex border-2 border-ink shadow-brut w-fit mb-6 bg-white">
                <button onClick={() => setTab("purchases")} className={`px-5 py-3 font-bold uppercase tracking-wider text-sm border-r-2 border-ink ${tab === "purchases" ? "bg-mint" : "bg-white"}`} data-testid="admin-tab-purchases">
                    Validar pagos ({purchases.filter((p) => p.status === "pending").length})
                </button>
                <button onClick={() => setTab("raffles")} className={`px-5 py-3 font-bold uppercase tracking-wider text-sm ${tab === "raffles" ? "bg-mint" : "bg-white"}`} data-testid="admin-tab-raffles">
                    Rifas
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin" /></div>
            ) : tab === "purchases" ? (
                <div className="space-y-4" data-testid="admin-purchases-list">
                    {purchases.length === 0 ? (
                        <div className="brut-card p-10 text-center">Sin compras registradas todavía.</div>
                    ) : (
                        purchases.map((p) => {
                            const cfg = PURCHASE_STATUS[p.status] || PURCHASE_STATUS.pending;
                            return (
                                <div key={p.id} className="brut-card p-5 grid md:grid-cols-12 gap-4 items-center" data-testid={`admin-purchase-${p.id}`}>
                                    <div className="md:col-span-4">
                                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Comprador</div>
                                        <div className="font-bold">{p.user_name}</div>
                                        <div className="text-xs opacity-70">{p.user_email}</div>
                                        <Link to={`/rifas/${p.raffle_id}`} className="text-xs underline">{p.raffle_title}</Link>
                                    </div>
                                    <div className="md:col-span-3">
                                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Boletos</div>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {p.ticket_numbers.map((n) => (<span key={n} className="border-2 border-ink bg-mint px-1.5 py-0.5 text-xs font-bold">#{n}</span>))}
                                        </div>
                                    </div>
                                    <div className="md:col-span-2">
                                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Monto</div>
                                        <div className="font-display font-black text-xl">S/ {p.amount.toFixed(2)}</div>
                                        {p.operation_code && <div className="text-[10px] mt-1 opacity-70">Op: {p.operation_code}</div>}
                                    </div>
                                    <div className="md:col-span-3 flex gap-2 flex-wrap justify-end">
                                        <button onClick={() => setViewReceipt(backendImg(p.receipt_url))} className="brut-btn text-xs" data-testid={`view-receipt-${p.id}`}>
                                            <Eye className="w-3 h-3" strokeWidth={3} /> Comprobante
                                        </button>
                                        {p.status === "pending" ? (
                                            <>
                                                <button onClick={() => act(`/admin/purchases/${p.id}/validate`, "Pago validado")} className="brut-btn brut-btn-mint text-xs" data-testid={`validate-${p.id}`}>
                                                    <CheckCircle2 className="w-3 h-3" strokeWidth={3} /> Validar
                                                </button>
                                                <button onClick={() => act(`/admin/purchases/${p.id}/reject`, "Pago rechazado")} className="brut-btn text-xs" data-testid={`reject-payment-${p.id}`}>
                                                    <XCircle className="w-3 h-3" strokeWidth={3} /> Rechazar
                                                </button>
                                            </>
                                        ) : (
                                            <div className={`border-2 border-ink ${cfg.cls} px-3 py-2 text-xs font-bold uppercase tracking-widest`}>{cfg.label}</div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            ) : (
                <div className="space-y-4" data-testid="admin-raffles-list">
                    {raffles.length === 0 ? (
                        <div className="brut-card p-10 text-center">Sin rifas creadas todavía.</div>
                    ) : (
                        raffles.map((r) => {
                            const sCfg = RAFFLE_STATUS[r.status] || RAFFLE_STATUS.pending;
                            return (
                                <div key={r.id} className="brut-card p-5 grid md:grid-cols-12 gap-4 items-center" data-testid={`admin-raffle-${r.id}`}>
                                    <div className="md:col-span-5">
                                        <div className={`inline-block border-2 border-ink ${sCfg.cls} px-2 py-1 text-[10px] uppercase tracking-widest font-bold mb-2`}>{sCfg.label}</div>
                                        <Link to={`/rifas/${r.id}`} className="font-display font-black text-xl hover:underline block">{r.title}</Link>
                                        <div className="text-xs opacity-70">{r.prize}</div>
                                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mt-1">Por: {r.creator_name}</div>
                                    </div>
                                    <div className="md:col-span-3">
                                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Vendidos</div>
                                        <div className="font-display font-black text-xl">{r.tickets_sold}/{r.total_tickets}</div>
                                        <div className="text-xs">S/ {r.price_per_ticket.toFixed(2)} c/u</div>
                                    </div>
                                    <div className="md:col-span-4 flex gap-2 flex-wrap justify-end">
                                        {r.status === "pending" && (
                                            <>
                                                <button onClick={() => act(`/admin/raffles/${r.id}/approve`, "Rifa aprobada")} className="brut-btn brut-btn-mint text-xs" data-testid={`approve-raffle-${r.id}`}>
                                                    <CheckCircle2 className="w-3 h-3" strokeWidth={3} /> Aprobar
                                                </button>
                                                <button onClick={() => act(`/admin/raffles/${r.id}/reject`, "Rifa rechazada")} className="brut-btn text-xs" data-testid={`reject-raffle-${r.id}`}>
                                                    <XCircle className="w-3 h-3" strokeWidth={3} /> Rechazar
                                                </button>
                                            </>
                                        )}
                                        {r.status === "approved" && (
                                            <button onClick={() => drawWinner(r.id)} className="brut-btn brut-btn-primary text-xs" data-testid={`draw-${r.id}`}>
                                                <Trophy className="w-3 h-3" strokeWidth={3} /> Sortear
                                            </button>
                                        )}
                                        {r.status === "drawn" && (
                                            <div className="border-2 border-ink bg-coral text-white px-3 py-2 text-xs font-bold uppercase tracking-widest">
                                                Ganador #{r.winner_number}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {viewReceipt && (
                <div className="fixed inset-0 bg-ink/70 z-50 flex items-center justify-center p-4" onClick={() => setViewReceipt(null)} data-testid="receipt-modal">
                    <div className="brut-card bg-white p-4 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-3">
                            <div className="font-display font-black text-xl flex items-center gap-2"><FileText className="w-5 h-5" /> Comprobante Yape</div>
                            <button onClick={() => setViewReceipt(null)} className="brut-btn text-xs">Cerrar</button>
                        </div>
                        <img src={viewReceipt} alt="Comprobante" className="w-full border-2 border-ink" />
                    </div>
                </div>
            )}
        </div>
    );
}
